import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem, useDatabaseData } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { zodResolver } from "@hookform/resolvers/zod"
import { AlarmClock, Plus, Trash2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { useMemo, useState, useEffect } from "react"

export const jobs_crud = {
  title: "Job",
  route: "/job/:id",
  component,
  resource: "jobs",
  icon: AlarmClock,
  action: "create",
} satisfies PageConfig

const FormSchema = z.object({
  title: z.string(),
  agent: z.number(),
  type: z.number(),
  range: z.number(),
  day: z.number().optional().nullable(),
  sun: z.boolean().optional().nullable(),
  mon: z.boolean().optional().nullable(),
  tue: z.boolean().optional().nullable(),
  wed: z.boolean().optional().nullable(),
  thu: z.boolean().optional().nullable(),
  fri: z.boolean().optional().nullable(),
  sat: z.boolean().optional().nullable(),
})

type FormData = z.infer<typeof FormSchema>

type ParameterField = {
  name: string
  type: "text" | "number" | "boolean"
  value: string | number | boolean
}

const parameterTypeOptions = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
]

const typeOptions = [
  { value: 0, label: "Hourly" },
  { value: 1, label: "Daily" },
  { value: 2, label: "Monthly" },
]

const rangeOptions = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')}:00 to ${String((i + 1) % 24).padStart(2, '0')}:00`,
}))

const daysOfWeek = [
  { key: "sun", label: "Sunday" },
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
]

export function component() {
  const {
    data,
    loading,
    submit,
    toastSuccess,
    navigate,
  } = useDatabaseItem("jobs")

  const { selectedTeam } = useTeamStore()
  const { data: agents, loading: agentsLoading } = useDatabaseData("agents")

  const filteredAgents = useMemo(() => {
    if (!agents || !selectedTeam?.id) return []
    return agents.filter(agent => agent.team_id === selectedTeam.id)
  }, [agents, selectedTeam?.id])

  const [parameters, setParameters] = useState<ParameterField[]>([])

  console.log(rangeOptions)


  useEffect(() => {
    if (data?.parameters) {
      const params = typeof data.parameters === 'string'
        ? JSON.parse(data.parameters)
        : data.parameters

      if (params && typeof params === 'object') {
        const paramArray: ParameterField[] = Object.entries(params).map(([name, value]) => {
          const type = typeof value === 'boolean' ? 'boolean' : typeof value === 'number' ? 'number' : 'text'
          return {
            name,
            type: type as "text" | "number" | "boolean",
            value: value as string | number | boolean
          }
        })
        setParameters(paramArray)
      }
    }
  }, [data])

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data,
    values: data,
  })

  console.log(form.formState.errors)


  const selectedType = form.watch("type")

  const addParameter = () => {
    setParameters([...parameters, { name: "", type: "text", value: "" }])
  }

  const removeParameter = (index: number) => {
    setParameters(parameters.filter((_, i) => i !== index))
  }

  const updateParameter = (index: number, field: keyof ParameterField, value: any) => {
    const newParameters = [...parameters]
    newParameters[index] = { ...newParameters[index], [field]: value }

    if (field === 'type') {
      if (value === 'boolean') {
        newParameters[index].value = false
      } else if (value === 'number') {
        newParameters[index].value = 0
      } else {
        newParameters[index].value = ""
      }
    }

    setParameters(newParameters)
  }

  async function onSubmit(data: FormData) {
    const parametersObject = parameters.reduce((acc, param) => {
      if (param.name) {
        acc[param.name] = param.value
      }
      return acc
    }, {} as Record<string, any>)

    const submitData = {
      ...data,
      team: selectedTeam?.id,
      parameters: parametersObject
    }

    console.log("Submit: ", submitData)
    await submit(submitData)
    toastSuccess()
    navigate("/jobs")
  }

  if (loading || agentsLoading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="jobs" onSuccess={"/jobs"} />
          </PageTitle>

          <div className="rgrid">

            <div className="row">

              <div className="rc-4">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="rc-4">
                <FormField control={form.control} name="agent" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent</FormLabel>
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an agent" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filteredAgents.map((agent) => (
                          <SelectItem key={agent.id} value={String(agent.id)}>
                            {agent.title || `Agent ${agent.id}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="rc-2">
                <FormField control={form.control} name="type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {typeOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-2">
                <FormField control={form.control} name="range" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Range</FormLabel>
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a range" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {rangeOptions.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            {selectedType === 1 && (
              <div className="row">
                <div className="rc-12">
                  <FormLabel>Days of Week</FormLabel>
                  <div className="flex gap-2 mt-2">
                    {daysOfWeek.map((day) => (
                      <FormField
                        key={day.key}
                        control={form.control}
                        name={day.key as keyof FormData}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Button type="button" variant={field.value ? "default" : "outline"} onClick={() => field.onChange(!field.value)} className="w-full">
                                {day.label}
                              </Button>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {selectedType === 2 && (
              <div className="row">
                <div className="rc-2">
                  <FormField control={form.control} name="day" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Day of Month</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} max={31} {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            <div className="row">
              <div className="rc-12">
                <div className="flex items-center justify-between mb-4">
                  <FormLabel>Parameters</FormLabel>
                  <Button type="button" variant="outline" size="sm" onClick={addParameter}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Parameter
                  </Button>
                </div>

                {parameters.length === 0 && (
                  <div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-md">
                    No parameters defined. Click "Add Parameter" to create one.
                  </div>
                )}

                {parameters.map((param, index) => (
                  <div key={index} className="row mb-4 p-4 border rounded-md">
                    <div className="rc-3">
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            value={param.name}
                            onChange={(e) => updateParameter(index, 'name', e.target.value)}
                            placeholder="parameter_name"
                          />
                        </FormControl>
                      </FormItem>
                    </div>
                    <div className="rc-2">
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select
                          value={param.type}
                          onValueChange={(value) => updateParameter(index, 'type', value)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {parameterTypeOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    </div>
                    <div className="rc-6">
                      <FormItem>
                        <FormLabel>Value</FormLabel>
                        <FormControl>
                          {param.type === 'boolean' ? (
                            <div className="flex items-center h-10">
                              <Checkbox
                                checked={param.value as boolean}
                                onCheckedChange={(checked) => updateParameter(index, 'value', checked)}
                              />
                              <span className="ml-2 text-sm">{param.value ? 'True' : 'False'}</span>
                            </div>
                          ) : param.type === 'number' ? (
                            <Input
                              type="number"
                              value={param.value as number}
                              onChange={(e) => updateParameter(index, 'value', Number(e.target.value))}
                            />
                          ) : (
                            <Input
                              type="text"
                              value={param.value as string}
                              onChange={(e) => updateParameter(index, 'value', e.target.value)}
                            />
                          )}
                        </FormControl>
                      </FormItem>
                    </div>
                    <div className="rc-1 flex items-end">
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => removeParameter(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>
}
